import {
  DownOutlined,
  GlobalOutlined,
  LogoutOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { history, setLocale, useModel, useRequest } from '@umijs/max';
import { Avatar, Button, Dropdown, message, Space, theme } from 'antd';
import type { MenuProps } from 'antd';
import React from 'react';
import { flushSync } from 'react-dom';
import { loginModal } from '@/components/LoginModal';
import { paymentModal } from '@/components/PaymentModal';
import {
  DASHBOARD_DATA_CACHE_KEY,
  extractDashboardLedgerBalance,
  fetchDashboardData,
} from '@/services/dashboard';
import { outLogin } from '@/services/auth';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';

const LANGUAGE_LABELS = {
  'zh-CN': '简体中文',
  'en-US': 'English',
} as const;

const SiderFooter: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { token } = theme.useToken();
  const { initialState, setInitialState } = useModel('@@initialState');

  const isLoggedIn = !!initialState?.currentUser;
  const userId = initialState?.currentUser?.user?.id;
  const currentLocale = getAppLocale();

  const { data: dashboardData } = useRequest(
    () => fetchDashboardData({ skipErrorHandler: true }),
    {
      ready: Boolean(userId),
      cacheKey: DASHBOARD_DATA_CACHE_KEY,
      staleTime: 5_000,
      refreshOnWindowFocus: false,
    },
  );

  const balance = React.useMemo(() => {
    if (!userId) {
      return 0;
    }
    return extractDashboardLedgerBalance(dashboardData);
  }, [dashboardData, userId]);

  const balanceText = React.useMemo(() => {
    const amount = Number(balance || 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }

    return new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [balance, currentLocale]);

  const loginOut = async () => {
    localStorage.removeItem('token');
    await outLogin();
    message.success(t('components.siderFooter.logoutSuccess', 'Logged out successfully'));
    history.replace('/home');
  };

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key !== 'logout') {
      return;
    }

    flushSync(() => {
      setInitialState((state) => ({ ...state, currentUser: undefined }));
    });

    loginOut();
  };

  const onLanguageMenuClick: MenuProps['onClick'] = ({ key }) => {
    const nextLocale = String(key);
    if (nextLocale !== currentLocale) {
      setLocale(nextLocale, false);
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('components.siderFooter.logout', 'Logout'),
    },
  ];

  const languageItems: MenuProps['items'] = Object.entries(LANGUAGE_LABELS).map(([key, label]) => ({
    key,
    label,
  }));

  const borderColor = `color-mix(in srgb, ${token.colorBorderSecondary} 82%, ${token.colorBgContainer})`;
  const hoverBackground = `color-mix(in srgb, ${token.colorFillSecondary} 88%, ${token.colorBgContainer})`;
  const metricIconBackground = `color-mix(in srgb, ${token.colorPrimaryBg} 90%, ${token.colorBgContainer})`;

  const iconSlotStyle: React.CSSProperties = {
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const metricIconStyle: React.CSSProperties = {
    ...iconSlotStyle,
    borderRadius: token.borderRadius,
    color: token.colorPrimary,
    background: metricIconBackground,
    fontSize: 14,
    alignSelf: 'flex-start',
  };

  const metricMetaStyle: React.CSSProperties = {
    minWidth: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
    paddingTop: 1,
  };

  const metricLabelStyle: React.CSSProperties = {
    fontSize: 12,
    lineHeight: '16px',
    color: token.colorTextTertiary,
  };

  const metricValueStyle: React.CSSProperties = {
    fontSize: 20,
    lineHeight: '24px',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: token.colorTextHeading,
  };

  const metricActionStyle: React.CSSProperties = {
    marginTop: 2,
    padding: 0,
    height: 20,
    fontWeight: 500,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    minHeight: 38,
    padding: '0',
    cursor: 'pointer',
    borderRadius: token.borderRadiusLG,
    transition: 'background-color 0.2s ease, color 0.2s ease',
  };

  const rowLeadStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    flex: 1,
    paddingRight: 4,
  };

  const rowLabelStyle: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 14,
    color: token.colorText,
  };

  const plainIconStyle: React.CSSProperties = {
    fontSize: 14,
    lineHeight: 1,
    color: token.colorTextSecondary,
  };

  const arrowSlotStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: token.colorTextTertiary,
  };

  const collapsedActionStyle: React.CSSProperties = {
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: token.borderRadiusLG,
    cursor: 'pointer',
    color: token.colorTextSecondary,
    transition: 'background-color 0.2s ease, color 0.2s ease',
  };

  const handleInteractiveEnter = (event: React.MouseEvent<HTMLElement>) => {
    event.currentTarget.style.background = hoverBackground;
  };

  const handleInteractiveLeave = (event: React.MouseEvent<HTMLElement>) => {
    event.currentTarget.style.background = 'transparent';
  };

  const userName = initialState?.currentUser?.user?.name || t('components.siderFooter.notLoggedIn', 'Not signed in');
  const currentLanguageLabel = LANGUAGE_LABELS[currentLocale as keyof typeof LANGUAGE_LABELS] || currentLocale;

  const expandedUserRow = isLoggedIn ? (
    <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} placement="topRight" trigger={['click']}>
      <div style={rowStyle} onMouseEnter={handleInteractiveEnter} onMouseLeave={handleInteractiveLeave}>
        <div style={rowLeadStyle}>
          <span style={iconSlotStyle}>
            <Avatar
              size={18}
              icon={<UserOutlined />}
              src={initialState?.currentUser?.user?.avatar}
              style={{ flexShrink: 0, backgroundColor: token.colorPrimary }}
            />
          </span>
          <span
            style={{
              ...rowLabelStyle,
              fontWeight: 500,
              color: token.colorTextHeading,
            }}
          >
            {userName}
          </span>
        </div>
        <span style={arrowSlotStyle}>
          <DownOutlined style={{ fontSize: 10 }} />
        </span>
      </div>
    </Dropdown>
  ) : (
    <div
      style={rowStyle}
      onClick={() => loginModal.show()}
      onMouseEnter={handleInteractiveEnter}
      onMouseLeave={handleInteractiveLeave}
    >
      <div style={rowLeadStyle}>
        <span style={iconSlotStyle}>
          <UserOutlined style={plainIconStyle} />
        </span>
        <span style={rowLabelStyle}>{t('components.siderFooter.login', 'Login')}</span>
      </div>
    </div>
  );

  if (collapsed) {
    return (
      <div
        style={{
          padding: '8px 0 10px',
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }} align="center">
          <div
            style={collapsedActionStyle}
            onClick={() => paymentModal.show({ defaultTab: 'plans' })}
            onMouseEnter={handleInteractiveEnter}
            onMouseLeave={handleInteractiveLeave}
            title={t('components.siderFooter.subscriptionAndRecharge', 'Subscription & Recharge')}
          >
            <WalletOutlined style={{ fontSize: 15 }} />
          </div>

          <Dropdown
            menu={{ items: languageItems, onClick: onLanguageMenuClick, selectedKeys: [currentLocale] }}
            placement="topRight"
            trigger={['click']}
          >
            <div
              style={collapsedActionStyle}
              onMouseEnter={handleInteractiveEnter}
              onMouseLeave={handleInteractiveLeave}
              title={currentLanguageLabel}
            >
              <GlobalOutlined style={{ fontSize: 15 }} />
            </div>
          </Dropdown>

          {isLoggedIn ? (
            <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} placement="topRight" trigger={['click']}>
              <div style={collapsedActionStyle} onMouseEnter={handleInteractiveEnter} onMouseLeave={handleInteractiveLeave}>
                <Avatar
                  size={20}
                  icon={<UserOutlined />}
                  src={initialState?.currentUser?.user?.avatar}
                  style={{ backgroundColor: token.colorPrimary }}
                />
              </div>
            </Dropdown>
          ) : (
            <div
              style={collapsedActionStyle}
              onClick={() => loginModal.show()}
              onMouseEnter={handleInteractiveEnter}
              onMouseLeave={handleInteractiveLeave}
              title={t('components.siderFooter.login', 'Login')}
            >
              <UserOutlined style={{ fontSize: 15 }} />
            </div>
          )}
        </Space>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '10px 0 14px',
        borderTop: `1px solid ${borderColor}`,
      }}
    >
      <div
        style={{
          padding: '2px 0 10px',
          marginBottom: 2,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
          <span style={metricIconStyle}>
            <ThunderboltOutlined />
          </span>
          <div style={metricMetaStyle}>
            <div style={metricLabelStyle}>{t('components.siderFooter.credits', 'Credits')}</div>
            <div style={metricValueStyle}>{balanceText}</div>
            <Button
              type="link"
              size="small"
              style={metricActionStyle}
              onClick={() => paymentModal.show({ defaultTab: 'recharge' })}
            >
              {t('components.siderFooter.subscribeRecharge', 'Subscribe / Recharge')}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 2 }}>
        <Dropdown
          menu={{ items: languageItems, onClick: onLanguageMenuClick, selectedKeys: [currentLocale] }}
          trigger={['click']}
          placement="topRight"
        >
          <div style={rowStyle} onMouseEnter={handleInteractiveEnter} onMouseLeave={handleInteractiveLeave}>
            <div style={rowLeadStyle}>
              <span style={iconSlotStyle}>
                <GlobalOutlined style={plainIconStyle} />
              </span>
              <span style={rowLabelStyle}>{currentLanguageLabel}</span>
            </div>
            <span style={arrowSlotStyle}>
          <DownOutlined style={{ fontSize: 10 }} />
        </span>
          </div>
        </Dropdown>

        {expandedUserRow}
      </div>
    </div>
  );
};

export default SiderFooter;
